// defineComponent komponiert eine Liste von Mixins zu einer
// HTMLElement-Subclass + ruft customElements.define() auf.
//
// Pattern: jeder Mixin bekommt einen Hook-Slot (connectedCallback,
// disconnectedCallback). Mixins werden in der definierten Reihenfolge
// ausgeloest. Render-Funktion wird nach allen Connect-Mixins aufgerufen.

export interface ComponentContext {
  shadowRoot: ShadowRoot | null;
  cleanup: Array<() => void>;
  // rerender: re-run render() ohne Re-Connect — der Seam fuer reaktive Mixins
  // (z.B. withI18n bei Locale-Wechsel). Fuehrt die render-registrierten cleanups
  // aus + resettet sie, leert den ShadowRoot, ruft render() erneut. Mixins
  // (connect/disconnect) laufen NICHT erneut — sie persistieren. Vor connect ist
  // es ein No-op. Wird von defineComponent gesetzt.
  rerender: () => void;
}

export type Mixin = (host: HTMLElement, ctx: ComponentContext) => {
  connect?: () => void | Promise<void>;
  disconnect?: () => void;
};

export interface ComponentDefinition {
  tagName: string;
  mixins?: Mixin[];
  // created läuft im Konstruktor — der EINZIGE Seam, der VOR connect/render
  // greift. Hier gehören Property-Upgrades hin, die schon an einem detachten
  // Element funktionieren müssen (z.B. pre-connect-sicherer `.value` via
  // installFieldValue). Spec-Regel: im Konstruktor KEINE Attribute/Kinder
  // inspizieren — nur Accessor installieren (lazy lesen bei get).
  created?: (host: HTMLElement) => void;
  render?: (host: HTMLElement, ctx: ComponentContext) => void;
  // eager: SYNCHRON registrieren statt via queueMicrotask. Nötig, wenn eine
  // Geschwister-WC im SELBEN Bundle diese WC SYNCHRON konsumiert (z.B.
  // server-gerenderte Cut-App-WC upgradet mid-eval + baut sofort <pfs-field>).
  // Voraussetzung: STYLES/Consts stehen VOR dem defineComponent-Aufruf (sonst
  // greift das Module-Order-Problem, das die Deferral löst).
  eager?: boolean;
}

export function defineComponent(def: ComponentDefinition): void {
  if (customElements.get(def.tagName)) return;

  class PfsComponent extends HTMLElement {
    private _ctx: ComponentContext = {
      shadowRoot: null,
      cleanup: [],
      rerender: () => this._rerender(),
    };
    private _mixinHandles: ReturnType<Mixin>[] = [];
    private _connected = false;

    constructor() {
      super();
      // created = Konstruktor-Seam: Accessor-Upgrades, die pre-connect greifen.
      def.created?.(this);
    }

    connectedCallback(): void {
      const mixins = def.mixins ?? [];
      this._mixinHandles = mixins.map((m) => m(this, this._ctx));
      for (const h of this._mixinHandles) h.connect?.();
      this._connected = true;
      def.render?.(this, this._ctx);
    }

    // Re-Render ohne Re-Connect (reaktive Mixins). Mixins persistieren; nur die
    // render-Ebene laeuft frisch (cleanups raus, Shadow leeren, render erneut).
    private _rerender(): void {
      if (!this._connected) return;
      for (const fn of this._ctx.cleanup) fn();
      this._ctx.cleanup = [];
      this._ctx.shadowRoot?.replaceChildren();
      def.render?.(this, this._ctx);
    }

    disconnectedCallback(): void {
      this._connected = false;
      for (const h of this._mixinHandles) h.disconnect?.();
      for (const fn of this._ctx.cleanup) fn();
      this._ctx.cleanup = [];
      this._mixinHandles = [];
    }
  }

  // customElements.define triggert sofort die "upgrade reaction" für alle
  // bereits im DOM existierenden Light-DOM-Hosts (server-rendered HTML).
  // Diese Upgrade-Reaktion ruft connectedCallback() synchron auf — bevor
  // das umgebende ESM-Modul fertig evaluiert ist. Module-Level
  // const/var-Bodies, die NACH defineComponent() deklariert sind (z.B.
  // STYLES = `...`), sind zu diesem Zeitpunkt noch undefined → leerer
  // <style>-Tag, kein Styling.
  //
  // queueMicrotask schiebt die Registrierung an den Ende des aktuellen
  // Module-Evaluations-Job. Damit ist garantiert, dass alle Top-Level-
  // Konstanten initialisiert sind, bevor render() läuft. Source-Order
  // im Component-File wird irrelevant.
  if (def.eager) {
    // synchron: WC ist ab dem Import sofort verfügbar (fuer synchron
    // konsumierte Geschwister-WCs). STYLES muessen VOR diesem Aufruf stehen.
    customElements.define(def.tagName, PfsComponent);
    return;
  }
  queueMicrotask(() => {
    if (customElements.get(def.tagName)) return;
    customElements.define(def.tagName, PfsComponent);
  });
}
